import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Truck, User, Building2, Mail, Lock, Phone, MapPin, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

export default function RegisterPage() {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [searchParams] = useSearchParams();
  // Only business accounts allowed - ignore URL param
  const [activeTab, setActiveTab] = useState("business");
  const [loading, setLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    confirmPassword: "",
    name: "",
    company_name: "",
    address: "",
    postal_code: "",
    city: "",
    phone: ""
  });

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error("Passwörter stimmen nicht überein");
      return;
    }
    
    if (formData.password.length < 6) {
      toast.error("Passwort muss mindestens 6 Zeichen haben");
      return;
    }
    
    setLoading(true);
    
    try {
      const userData = {
        email: formData.email,
        password: formData.password,
        name: activeTab === "business" ? formData.company_name : formData.name,
        role: activeTab,
        company_name: activeTab === "business" ? formData.company_name : null,
        address: formData.address || null,
        postal_code: formData.postal_code || null,
        city: formData.city || null,
        phone: formData.phone || null
      };
      
      const user = await register(userData);
      
      if (activeTab === "business") {
        toast.success(
          "Ihr Business-Account wurde erfolgreich erstellt! Ihr Konto wird jetzt von unserem Team geprüft. Unser Support wird sich in Kürze bei Ihnen melden.",
          { duration: 8000 }
        );
      }
      
      navigate("/dashboard");
    } catch (err) {
      // Error handled in auth context
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Header */}
      <header className="bg-slate-900 text-white py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/" className="flex items-center gap-3 w-fit">
            <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
              <Truck className="h-6 w-6 text-white" />
            </div>
            <div>
              <span className="font-bold text-lg">SonderCargo.de</span>
              <span className="text-xs text-slate-400 block">Medical, Weapons and much more</span>
            </div>
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center p-4 py-8">
        <div className="w-full max-w-lg">
          <Link to="/" className="inline-flex items-center text-slate-600 hover:text-slate-900 mb-6" data-testid="back-link">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Zurück zur Startseite
          </Link>
          
          <Card className="border-0 shadow-lg" data-testid="register-card">
            <CardHeader className="text-center pb-2">
              <CardTitle className="text-2xl font-bold text-slate-900" data-testid="register-title">
                Konto erstellen
              </CardTitle>
              <CardDescription className="text-slate-500">
                Wählen Sie Ihren Kontotyp
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                <TabsList className="grid w-full grid-cols-2 mb-6" data-testid="account-type-tabs">
                  <TabsTrigger 
                    value="private" 
                    className="flex items-center gap-2 opacity-50 cursor-not-allowed" 
                    disabled
                    data-testid="tab-private"
                  >
                    <User className="h-4 w-4" />
                    Privat
                    <span className="text-xs ml-1">(deaktiviert)</span>
                  </TabsTrigger>
                  <TabsTrigger value="business" className="flex items-center gap-2" data-testid="tab-business">
                    <Building2 className="h-4 w-4" />
                    Business
                  </TabsTrigger>
                </TabsList>
                
                <form onSubmit={handleSubmit} className="space-y-4">
                  <TabsContent value="private" className="space-y-4 mt-0">
                    <div className="space-y-2">
                      <Label htmlFor="name">Name *</Label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <Input
                          id="name"
                          placeholder="Max Mustermann"
                          value={formData.name}
                          onChange={(e) => updateField("name", e.target.value)}
                          className="pl-10"
                          required={activeTab === "private"}
                          data-testid="name-input"
                        />
                      </div>
                    </div>
                  </TabsContent>
                  
                  <TabsContent value="business" className="space-y-4 mt-0">
                    <div className="space-y-2">
                      <Label htmlFor="company_name">Firmenname *</Label>
                      <div className="relative">
                        <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <Input
                          id="company_name"
                          placeholder="Muster GmbH"
                          value={formData.company_name}
                          onChange={(e) => updateField("company_name", e.target.value)}
                          className="pl-10"
                          required={activeTab === "business"}
                          data-testid="company-input"
                        />
                      </div>
                    </div>
                    
                    <div className="p-3 bg-blue-50 rounded-lg text-sm text-blue-800" data-testid="business-info">
                      <strong>Business-Vorteile:</strong>
                      <ul className="list-disc list-inside mt-1 text-blue-700">
                        <li>Kauf auf Rechnung</li>
                        <li>Mögliche Pauschalpreise</li>
                        <li>Monatliche Abrechnung</li>
                      </ul>
                    </div>
                  </TabsContent>
                  
                  {/* Common fields */}
                  <div className="space-y-2">
                    <Label htmlFor="email">E-Mail *</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <Input
                        id="email"
                        type="email"
                        placeholder="ihre@email.de"
                        value={formData.email}
                        onChange={(e) => updateField("email", e.target.value)}
                        className="pl-10"
                        required
                        data-testid="email-input"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="password">Passwort *</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <Input
                          id="password"
                          type="password"
                          placeholder="••••••••"
                          value={formData.password}
                          onChange={(e) => updateField("password", e.target.value)}
                          className="pl-10"
                          required
                          data-testid="password-input"
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="confirmPassword">Bestätigen *</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <Input
                          id="confirmPassword"
                          type="password"
                          placeholder="••••••••"
                          value={formData.confirmPassword}
                          onChange={(e) => updateField("confirmPassword", e.target.value)}
                          className="pl-10"
                          required
                          data-testid="confirm-password-input"
                        />
                      </div>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="phone">Telefon</Label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <Input
                        id="phone"
                        placeholder="030 123456"
                        value={formData.phone}
                        onChange={(e) => updateField("phone", e.target.value)}
                        className="pl-10"
                        data-testid="phone-input"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-3 gap-4">
                    <div className="col-span-2 space-y-2">
                      <Label htmlFor="address">Straße</Label>
                      <Input
                        id="address"
                        placeholder="Musterstraße 1"
                        value={formData.address}
                        onChange={(e) => updateField("address", e.target.value)}
                        data-testid="address-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="postal_code">PLZ</Label>
                      <Input
                        id="postal_code"
                        placeholder="12345"
                        value={formData.postal_code}
                        onChange={(e) => updateField("postal_code", e.target.value)}
                        data-testid="postal-input"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="city">Stadt</Label>
                    <div className="relative">
                      <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <Input
                        id="city"
                        placeholder="Berlin"
                        value={formData.city}
                        onChange={(e) => updateField("city", e.target.value)}
                        className="pl-10"
                        data-testid="city-input"
                      />
                    </div>
                  </div>
                  
                  <Button 
                    type="submit" 
                    className="w-full bg-blue-600 hover:bg-blue-700"
                    disabled={loading}
                    data-testid="register-submit"
                  >
                    {loading ? (
                      <span className="flex items-center gap-2">
                        <div className="spinner" />
                        Registrieren...
                      </span>
                    ) : (
                      activeTab === "business" ? "Business Account beantragen" : "Jetzt registrieren"
                    )}
                  </Button>
                </form>
              </Tabs>
              
              <div className="mt-6 text-center">
                <p className="text-slate-600 text-sm">
                  Bereits registriert?{" "}
                  <Link to="/login" className="text-blue-600 hover:underline font-medium" data-testid="login-link">
                    Jetzt anmelden
                  </Link>
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
